import {showMessage} from 'exsportia-components'
import {Formik, FormikConfig, FormikProps} from 'formik'
import {DateTime} from 'luxon'
import {FC, useCallback, useMemo, useRef} from 'react'
import {useQuery} from 'react-query'
import {Outlet, useNavigate} from 'react-router-dom'
import {object} from 'yup'

import {isCachedOpnChargeDegraded, readOpnChargeState} from '../../../api/provider-status/get-provider-status.query'
import {prepareCyclesForShow} from '../../../api/subscription-bookings/helpers'
import {
  usePayNextSubscriptionCycle,
  usePaySubscriptionBookingCyclesByOneTimePayment,
  useUpdateSubscriptionPaymentMethod,
} from '../../../api/subscription-bookings/hooks'
import {CycleDraft} from '../../../api/subscription-bookings/mutations/create-subscription-booking-for-pay'
import {
  BookedCycle,
  SubscriptionBooking,
  SubscriptionBookingStatusesEnum,
} from '../../../api/subscription-bookings/types'
import {useVenue} from '../../../api/venue/hooks'
import {CycleOption, getAdditionalInstallmentsData} from '../../../pages/subscription-profile/helpers'
import {l} from '../../../utils/i18n'
import {guardChargeSubmit, hasCycleDueOnCardChange, resolveGateProvider} from '../../payments/helpers/checkout-gate'
import {PaymentTypes} from '../../payments/hooks/use-payment-options'
import {PaymentService} from '../../payments'
import {formatPayCycleError} from './pay-cycle-error'

export enum CyclePayTypeEnum {
  PAY_NOW = 'pay_now',
  SAVE_CARD = 'save_card'
}

export type PaySubscriptionCycleFormType = {
  subscriptionBooking: SubscriptionBooking | null
  paymentType: PaymentTypes | null,
  paymentMethodToken: string | null
  cyclePayType: CyclePayTypeEnum
  installmentOption: CycleOption['installmentsOptions'][number] | null
}

export const PaySubscriptionCyclesForm: FC = () => {
  const navigate = useNavigate()
  const {data: venue} = useVenue()
  const formikRef = useRef<FormikProps<PaySubscriptionCycleFormType>>(null)
  const {data: terminal} = useQuery(
    PaymentService.queries.getTerminalQuery({params: {venueId: venue?.uid}, refetchOnWindowFocus: false}),
  )

  const {mutate: paySubscriptionBookingCyclesByOneTimePayment} = usePaySubscriptionBookingCyclesByOneTimePayment({
    onSettled: () => {
      formikRef.current?.setSubmitting(false)
    },
    onMutate: () => {
      formikRef.current?.setSubmitting(true)
    },
  })

  const {mutate: updateSubscriptionPaymentMethod} = useUpdateSubscriptionPaymentMethod({
    onMutate: () => {
      formikRef.current?.setSubmitting(true)
    },
    onSuccess: (_, variables) => {
      if (venue) {
        navigate(`/${venue.uid}/subscription-bookings/${variables.subscriptionBookingId}/profile`, {replace: true})
      }
      showMessage(l('success'), 'success')
    },
    onError: () => {
      showMessage(l('somethingWentWrong'), 'error')
    },
    onSettled: () => {
      formikRef.current?.setSubmitting(false)
    }
  })

  const {
    mutate: payNextSubscriptionCycle,
  } = usePayNextSubscriptionCycle({
    onMutate: () => {
      formikRef.current?.setSubmitting(true)
    },
    onSuccess: (_, options) => {
      if (venue) {
        navigate(`/${venue.uid}/subscription-bookings/${options.subscriptionBookingId}/profile`, {replace: true})
      }
      showMessage(l('success'), 'success')
    },
    onError: (error) => {
      showMessage(formatPayCycleError(l, error, isCachedOpnChargeDegraded(venue?.uid)), 'error')
    },
    onSettled: () => {
      formikRef.current?.setSubmitting(false)
    }
  })

  const initialValues: PaySubscriptionCycleFormType = useMemo(
    () => ({
      installmentOption: null,
      paymentType: null,
      paymentMethodToken: null,
      subscriptionBooking: null,
      cyclePayType: CyclePayTypeEnum.PAY_NOW
    }),
    [],
  )

  const onSubmit = useCallback<FormikConfig<PaySubscriptionCycleFormType>['onSubmit']>((
    values,
    {setSubmitting}
    ) => {
    if (!values.subscriptionBooking || !venue) {
      setSubmitting(false)
      return
    }
    let additionalInstallmentsData = null
    if (values.installmentOption) {
      additionalInstallmentsData = getAdditionalInstallmentsData(values.installmentOption)
    }
    if (
      values.paymentType === PaymentTypes.PAY_NOW
      && values.paymentMethodToken
    ) {
      const subscriptionBookingId = values.subscriptionBooking.uid
      const paymentMethodId = values.paymentMethodToken
      if (values.cyclePayType === CyclePayTypeEnum.PAY_NOW) {
        // Checkout gate guarded here as well as on the page: whichever page mounted the form, a
        // saved-card cycle charge never leaves the app while Opn's charge op is DOWN.
        void guardChargeSubmit({
          provider: resolveGateProvider(terminal),
          paymentType: PaymentTypes.PAY_NOW,
          readChargeState: () => readOpnChargeState(venue.uid),
          submit: () => payNextSubscriptionCycle({
            subscriptionBookingId,
            data: {
              additionalPaymentData: {
                paymentMethodId,
                installmentAmount: additionalInstallmentsData?.installmentAmount || undefined,
              },
              venueId: venue.uid
            }
          }),
          onBlocked: () => {
            showMessage(l('checkout-gate-unavailable'), 'error')
            setSubmitting(false)
          },
        })
        return
      }
      const changeCard = () => updateSubscriptionPaymentMethod({
        subscriptionBookingId,
        data: {
          paymentMethodId,
          venueId: venue.uid,
          installmentAmount: additionalInstallmentsData?.installmentAmount || undefined,
        },
      })
      // changePaymentMethod charges the due cycles on save; a plain swap (nothing due) is no charge.
      if (!hasCycleDueOnCardChange(values.subscriptionBooking.cycles)) {
        changeCard()
        return
      }
      void guardChargeSubmit({
        provider: resolveGateProvider(terminal),
        paymentType: PaymentTypes.PAY_NOW,
        readChargeState: () => readOpnChargeState(venue.uid),
        submit: changeCard,
        onBlocked: () => {
          showMessage(l('checkout-gate-unavailable'), 'error')
          setSubmitting(false)
        },
      })
      return
    }

    if (values.paymentType === PaymentTypes.ONE_TIME) {
      const cyclesToShow = prepareCyclesForShow(
        values.subscriptionBooking.cycles,
        venue.timeZone
      )
      const endDates: number[] = []
      const cleanCycles = cyclesToShow.map((cycle) => {
        const {disabled: _, elIndex: __, ...rest} = cycle
        return rest as BookedCycle
      })

      const omitToPayCycles = cleanCycles.map((el) => {
        if (el.toPay) {
          endDates.push(el.endDate)
          return {
            ...el,
            isPaid: el.toPay,
            paymentType: 'EXSPORTIA_PAY',
            payDate: DateTime.now().toMillis(),
          } satisfies CycleDraft['cycles'][number]
        }
        const {toPay: _, ...rest} = el
        return rest as CycleDraft['cycles'][number]
      })

      const totalPrice = cleanCycles.reduce((acc, el) => {
        if (el.toPay) {
          return acc + el.price
        }
        return acc
      }, 0)

      const currentDate = DateTime.now().toMillis()
      const lastPaymentDate = endDates.reduce((maxDate, endDate) => Math.max(maxDate, endDate), endDates.length ? endDates[0] : 0)
      const checkActiveStatus = lastPaymentDate > currentDate

      const {venueId, memberId, userId, reference, uid} = values.subscriptionBooking
      const draft = {
        venueId,
        expireDate: lastPaymentDate,
        isPaid: false,
        lastPayment: lastPaymentDate,
        memberId,
        cycles: omitToPayCycles,
        userId,
        isPaidAdditionalFee: true,
        subscriptionBookingId: uid,
        reference,
        systemType: 'BOOKING_WEB',
        status: checkActiveStatus
          ? SubscriptionBookingStatusesEnum.ACTIVE
          : SubscriptionBookingStatusesEnum.INACTIVE,
      } satisfies CycleDraft
      paySubscriptionBookingCyclesByOneTimePayment({
        draft,
        entityId: uid,
        totalPrice
      })
      return
    }
    setSubmitting(false)
  }, [venue, terminal])
  const validationSchema = useMemo(
    () =>
      object().shape({
        subscriptionBooking: object().required(l('required')),
      }),
    [],
  )

  return (
    <>
      <Formik<PaySubscriptionCycleFormType>
        innerRef={formikRef}
        onSubmit={onSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
      >
        <Outlet />
      </Formik>
    </>
)
}
